import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { signOut } from 'firebase/auth';
import { collection, doc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { db, getAuthClient } from '../../firebaseConfig';
import { useAuth } from '../../auth/AuthProvider';
import { hasOnTimeAccess } from '../../auth/appAccess';
import { formatOnTimeRoleLabel, getAllowedOnTimePageKeys, getOnTimeLandingPath, normalizeOnTimeRole } from '../utils/access';
import { clearLinkProfileSession, clearOnTimeSession, writeLinkProfileSession } from '../auth/session';

const inputClass = 'link-access-input w-full rounded-2xl border border-cyan-300/15 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-cyan-300/50';

const DEFAULT_LINK_ADMIN_PIN = '000000';

function cleanPin(value = '') {
  return String(value || '').replace(/\D/g, '').slice(0, 6);
}

function profileName(profile = {}) {
  return String(profile.displayName || `${profile.fullName || ''} ${profile.surname || ''}`.trim() || profile.name || profile.email || 'Link profile').trim();
}

function splitEmailName(email = '') {
  const local = String(email || '').split('@')[0] || 'Link Admin';
  const cleaned = local.replace(/[._-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return cleaned ? cleaned.replace(/\b\w/g, (letter) => letter.toUpperCase()) : 'Link Admin';
}

function defaultAdminProfile({ user, claims, orgId }) {
  const email = String(user?.email || claims?.email || '').trim().toLowerCase();
  const uid = String(user?.uid || claims?.uid || '').trim();
  const name = String(claims?.displayName || user?.displayName || splitEmailName(email)).trim();

  return {
    id: uid ? `admin_${uid}` : email || 'link-admin',
    uid: uid || null,
    authUid: uid || null,
    ownerUid: uid || '',
    orgId,
    email,
    fullName: name,
    surname: '',
    displayName: name,
    accessPin: DEFAULT_LINK_ADMIN_PIN,
    pin: DEFAULT_LINK_ADMIN_PIN,
    linkPin: DEFAULT_LINK_ADMIN_PIN,
    onTimePin: DEFAULT_LINK_ADMIN_PIN,
    role: 'admin',
    onTimeRole: 'admin',
    onTimePages: ['control-tower', 'store-queue', 'driver-view', 'intake', 'customers', 'products', 'archives', 'reports', 'settings', 'profiles', 'my-view'],
    allowedPages: ['control-tower', 'store-queue', 'driver-view', 'intake', 'customers', 'products', 'archives', 'reports', 'settings', 'profiles', 'my-view'],
    tabsAllowed: ['control-tower', 'store-queue', 'driver-view', 'intake', 'customers', 'products', 'archives', 'reports', 'settings', 'profiles', 'my-view'],
    active: true,
    archived: false,
    removed: false,
    disabled: false,
    shared: false,
    isAuthAdminProfile: true,
    apps: ['ontime'],
    defaultApp: 'ontime',
    profileType: 'link-auth-admin',
  };
}

function toClaims(baseClaims = {}, profile = {}) {
  const role = normalizeOnTimeRole(profile.onTimeRole || profile.role || 'online-team');
  const pages = Array.isArray(profile.onTimePages)
    ? profile.onTimePages
    : Array.isArray(profile.allowedPages)
      ? profile.allowedPages
      : Array.isArray(profile.tabsAllowed)
        ? profile.tabsAllowed
        : [];
  const claims = {
    ...baseClaims,
    role,
    onTimeRole: role,
    linkRole: role,
    displayName: profileName(profile),
    fullName: String(profile.fullName || profile.name || profile.displayName || '').trim(),
    surname: String(profile.surname || '').trim(),
    phone: String(profile.phone || '').trim(),
    linkProfileId: String(profile.id || ''),
    linkProfileName: profileName(profile),
    onTimePages: pages,
    allowedPages: pages,
    tabsAllowed: pages,
    allowedOnTimePageKeys: getAllowedOnTimePageKeys({ ...baseClaims, role, onTimeRole: role, onTimePages: pages }),
    apps: Array.isArray(baseClaims.apps) ? baseClaims.apps : [],
    defaultApp: Array.isArray(baseClaims.apps) && baseClaims.apps.includes('ontime') ? 'ontime' : (baseClaims.defaultApp || ''),
    active: true,
  };
  return { ...claims, landingPath: getOnTimeLandingPath(claims) };
}

export default function LinkAccessLayer() {
  const { user, claims, refreshClaims } = useAuth();
  const orgId = String(claims?.orgId || '').trim();
  const [profiles, setProfiles] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [searchReady, setSearchReady] = useState(false);
  const fieldSalt = useMemo(() => `link-${String(user?.uid || user?.email || 'profile').replace(/[^a-z0-9]/gi, '')}-${Date.now()}`, [user?.uid, user?.email]);

  const selected = useMemo(() => profiles.find((profile) => profile.id === selectedId) || profiles[0] || null, [profiles, selectedId]);

  const visibleProfiles = useMemo(() => {
    const term = search.trim().toLowerCase();
    const isAdmin = (profile) => profile.isAuthAdminProfile === true || profile.isDefaultAdmin === true || profile.profileType === 'link-auth-admin' || profile.profileType === 'link-access-layer-admin' || normalizeOnTimeRole(profile.onTimeRole || profile.role) === 'admin';
    const adminProfiles = profiles.filter(isAdmin);

    if (!term) {
      return adminProfiles.length ? adminProfiles : profiles.slice(0, 1);
    }

    const matches = profiles.filter((profile) => {
      if (isAdmin(profile)) return true;
      const haystack = [profile.name, profile.displayName, profile.fullName, profile.surname, profile.email, profile.phone]
        .map((value) => String(value || '').toLowerCase())
        .join(' ');
      return haystack.includes(term);
    });

    return matches;
  }, [profiles, search]);

  const loadProfiles = useCallback(async () => {
    if (!orgId || !hasOnTimeAccess(claims)) {
      setProfiles([]);
      setLoading(false);
      setError(!orgId ? '' : 'This member does not have Link access enabled.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const profilesCol = collection(db, 'orgs', orgId, 'linkProfiles');
      const currentUid = String(user?.uid || claims?.uid || '').trim();
      const adminId = currentUid ? `admin_${currentUid}` : '';

      if (user?.email && currentUid) {
        const adminProfile = defaultAdminProfile({ user, claims, orgId });
        await setDoc(
          doc(db, 'orgs', orgId, 'linkProfiles', adminId),
          { ...adminProfile, id: adminId, createdAt: serverTimestamp(), updatedAt: serverTimestamp() },
          { merge: true }
        );
      }

      const sharedSnap = await getDocs(query(profilesCol, where('shared', '==', true)));
      const adminSnap = currentUid
        ? await getDocs(query(profilesCol, where('ownerUid', '==', currentUid), where('isAuthAdminProfile', '==', true)))
        : { docs: [] };

      const byId = new Map();
      [...adminSnap.docs, ...sharedSnap.docs].forEach((docSnap) => {
        const profile = { id: docSnap.id, ...(docSnap.data() || {}) };
        const active = profile.active !== false && profile.disabled !== true && profile.archived !== true && profile.removed !== true;
        if (!active) return;
        if (!(profile.accessPin || profile.pin || profile.linkPin || profile.onTimePin)) return;
        if ((profile.isAuthAdminProfile === true || profile.profileType === 'link-auth-admin') && String(profile.ownerUid || profile.uid || '').trim() !== currentUid) return;
        byId.set(profile.id, {
          ...profile,
          role: normalizeOnTimeRole(profile.onTimeRole || profile.role || 'online-team'),
          name: profileName(profile),
        });
      });

      const seenPrivateAdmin = new Set();
      const items = Array.from(byId.values()).filter((item) => {
        const isPrivateAdmin = item.isAuthAdminProfile === true || item.isDefaultAdmin === true || item.profileType === 'link-auth-admin' || item.profileType === 'link-access-layer-admin';
        if (!isPrivateAdmin) return true;
        const key = String(item.ownerUid || item.authUid || item.uid || currentUid || item.email || item.id);
        if (seenPrivateAdmin.has(key)) return false;
        seenPrivateAdmin.add(key);
        return true;
      }).sort((a, b) => {
        const aAdmin = a.isAuthAdminProfile === true || a.isDefaultAdmin === true || a.profileType === 'link-auth-admin' || a.profileType === 'link-access-layer-admin';
        const bAdmin = b.isAuthAdminProfile === true || b.isDefaultAdmin === true || b.profileType === 'link-auth-admin' || b.profileType === 'link-access-layer-admin';
        if (aAdmin && !bAdmin) return -1;
        if (!aAdmin && bAdmin) return 1;
        return a.name.localeCompare(b.name);
      });

      setProfiles(items);
      setSelectedId((current) => (items.some((item) => item.id === current) ? current : items[0]?.id || ''));
      if (!items.length) setError('No Link access profiles have been created yet. Create PIN profiles from Link → Profiles.');
    } catch (err) {
      console.error('[LinkAccessLayer] failed to load profiles', err);
      setError(err?.message || 'Could not load Link access profiles.');
    } finally {
      setLoading(false);
    }
  }, [claims, orgId, user]);

  useEffect(() => {
    setSearch('');
    setPin('');
    setSearchReady(false);
    loadProfiles();
    const clearBrowserFill = window.setTimeout(() => {
      setSearch('');
      setPin('');
      setSearchReady(true);
    }, 80);
    return () => window.clearTimeout(clearBrowserFill);
  }, [loadProfiles]);

  function resetAccessLayer() {
    clearLinkProfileSession();
    setSearch('');
    setPin('');
    setError('');
    setSelectedId((profiles.find((profile) => normalizeOnTimeRole(profile.onTimeRole || profile.role) === 'admin') || profiles[0] || {}).id || '');
  }

  async function logoutMatrixOS() {
    clearLinkProfileSession();
    clearOnTimeSession();
    setSearch('');
    setPin('');
    setError('');
    try {
      await signOut(getAuthClient());
    } finally {
      window.location.assign('/login');
    }
  }

  async function unlock(event) {
    event.preventDefault();
    if (!selected) return;
    const savedPin = cleanPin(selected.accessPin || selected.pin || selected.linkPin || selected.onTimePin);
    if (!savedPin || cleanPin(pin) !== savedPin) {
      setError('Incorrect PIN for this Link profile.');
      return;
    }
    const profileClaims = toClaims(claims, selected);
    writeLinkProfileSession({
      app: 'ontime',
      orgId,
      uid: String(user?.uid || ''),
      email: String(user?.email || '').trim().toLowerCase(),
      profileId: selected.id,
      profileName: selected.name,
      claims: profileClaims,
      landingPath: profileClaims.landingPath || getOnTimeLandingPath(profileClaims),
      unlockedAt: Date.now(),
    });
    await refreshClaims({ force: false });
  }

  return (
    <div className="min-h-screen overflow-hidden bg-[#04111f] text-white">
      <style>{`
        .link-access-input:-webkit-autofill,
        .link-access-input:-webkit-autofill:hover,
        .link-access-input:-webkit-autofill:focus,
        .link-access-input:-webkit-autofill:active {
          -webkit-text-fill-color: #ffffff !important;
          caret-color: #ffffff !important;
          box-shadow: 0 0 0 1000px rgba(2, 6, 23, 0.95) inset !important;
          transition: background-color 999999s ease-in-out 0s !important;
        }
      `}</style>
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(34,211,238,.18),transparent_30%),radial-gradient(circle_at_80%_35%,rgba(59,130,246,.16),transparent_26%),linear-gradient(135deg,#04111f,#071827_55%,#020617)]" />
      <div className="relative mx-auto grid min-h-screen max-w-6xl place-items-center px-4 py-10">
        <div className="grid w-full gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <section className="rounded-[2rem] border border-white/10 bg-white/[0.04] p-7 shadow-2xl backdrop-blur-xl">
            <div className="mb-4 inline-flex rounded-full border border-cyan-300/20 bg-cyan-300/10 px-3 py-1 text-xs uppercase tracking-[0.35em] text-cyan-200">Link access layer</div>
            <h1 className="text-4xl font-black leading-tight md:text-5xl">Choose your dispatch profile.</h1>
            <p className="mt-4 text-sm leading-7 text-slate-300">One authenticated MatrixOS account opens Link. Each team member then unlocks their own role, tabs, and dispatch view with a secure PIN.</p>
            <div className="mt-6 grid gap-3 text-sm text-slate-300">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4"><span className="text-cyan-200">01</span> Select a Link profile.</div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4"><span className="text-cyan-200">02</span> Enter the 6-digit access PIN.</div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4"><span className="text-cyan-200">03</span> Continue into the correct dispatch workspace.</div>
            </div>
          </section>

          <section className="rounded-[2rem] border border-cyan-300/20 bg-slate-950/50 p-7 shadow-2xl backdrop-blur-xl">
            <div className="mb-5">
              <div className="text-xs uppercase tracking-[0.35em] text-cyan-200">Profile unlock</div>
              <h2 className="mt-2 text-2xl font-black">Enter Link</h2>
              <p className="mt-2 text-sm text-slate-400">Signed in as {user?.email || 'MatrixOS user'}</p>
            </div>

            {error ? <div className="mb-4 rounded-2xl border border-amber-300/20 bg-amber-400/10 px-4 py-3 text-sm text-amber-100">{error}</div> : null}

            {loading ? (
              <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-8 text-center text-sm text-slate-300">Loading Link profiles…</div>
            ) : (
              <form onSubmit={unlock} className="space-y-4" autoComplete="off">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Search profile</label>
                  <input
                    className={inputClass}
                    type="search"
                    name={`${fieldSalt}-search`}
                    value={searchReady ? search : ''}
                    onFocus={() => { setSearchReady(true); setSearch(''); }}
                    onChange={(e) => { setSearchReady(true); setSearch(e.target.value); }}
                    placeholder="Search name, email, or role"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    data-lpignore="true"
                    data-1p-ignore="true"
                  />
                </div>

                <div className="grid gap-3">
                  {visibleProfiles.map((profile) => (
                    <button
                      type="button"
                      key={profile.id}
                      onClick={() => { setSelectedId(profile.id); setPin(''); setError(''); }}
                      className={`rounded-2xl border p-4 text-left transition ${selected?.id === profile.id ? 'border-cyan-300/60 bg-cyan-300/10 shadow-[0_0_28px_rgba(34,211,238,.15)]' : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10'}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-semibold text-white">{profile.name}</div>
                          <div className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-400">{formatOnTimeRoleLabel(profile.role)}</div>
                        </div>
                        <div className="grid h-10 w-10 place-items-center rounded-2xl bg-cyan-300/10 text-sm font-black text-cyan-100">{profile.name.slice(0, 2).toUpperCase()}</div>
                      </div>
                    </button>
                  ))}

                  {!visibleProfiles.length ? (
                    <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-5 text-center text-sm text-slate-300">No Link profiles match that search.</div>
                  ) : null}

                  {!search.trim() && profiles.length > visibleProfiles.length ? (
                    <div className="rounded-2xl border border-cyan-300/10 bg-cyan-300/5 px-4 py-3 text-sm text-slate-300">Search a name to unlock another Link profile. Admin stays visible by default.</div>
                  ) : null}
                </div>

                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Access PIN</label>
                  <input
                    className={inputClass}
                    name={`${fieldSalt}-pin`}
                    value={pin}
                    onFocus={() => setPin((current) => cleanPin(current))}
                    onChange={(e) => setPin(cleanPin(e.target.value))}
                    inputMode="numeric"
                    type="password"
                    maxLength={6}
                    placeholder="Enter 6-digit PIN"
                    autoComplete="new-password"
                    data-lpignore="true"
                    data-1p-ignore="true"
                  />
                </div>

                <button type="submit" disabled={!selected || cleanPin(pin).length !== 6} className="w-full rounded-2xl bg-cyan-300 px-5 py-3 text-sm font-black text-slate-950 transition hover:bg-cyan-200 disabled:cursor-not-allowed disabled:opacity-50">Unlock Link</button>

                <div className="flex items-center justify-between gap-3 text-xs">
                  <button type="button" onClick={resetAccessLayer} className="rounded-full border border-white/10 px-4 py-2 font-semibold text-slate-300 transition hover:bg-white/10 hover:text-white">Reset</button>
                  <button type="button" onClick={logoutMatrixOS} className="rounded-full border border-white/10 px-4 py-2 font-semibold text-slate-300 transition hover:bg-white/10 hover:text-white">Logout</button>
                </div>
              </form>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
